var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import * as Viewport from "pixi-viewport";
import { resize$ } from "../../events/eventsBrowser";
import { selectIsTutorialActive } from "../../selectors/selectTutorial";
import { shallowDiffers } from "../../utils/shallowDiffers";
import { utilNumber } from "../../utils/utilNumber";
import { utilTick } from "../../utils/utilTick";
import { CanvasConnectedComponent } from "../generic/CanvasConnectedComponent";
import { IdIsoSortingGroup } from "../iso/IdIsoSortingGroup";
import { IsoSorter } from "../iso/IsoSorter";
import { actionViewportRequestClearCenter } from "../viewport/actionViewport";
var Point = PIXI.Point;
var SceneCanvas = /** @class */ (function (_super) {
    __extends(SceneCanvas, _super);
    function SceneCanvas(config) {
        var _this = _super.call(this) || this;
        _this.isoSorterZIndex = Number.MAX_SAFE_INTEGER;
        _this.ViewportAnimationTimeMs = 200;
        _this.shouldEnableViewport = true;
        _this.isTutorialActive = true;
        _this.shouldUpdate = function (props, oldProps) {
            _this.enableViewport();
            return (!oldProps ||
                shallowDiffers(props.centerPosition, oldProps.centerPosition) ||
                shallowDiffers(props.zoomRequested, oldProps.zoomRequested));
        };
        _this.mapState = function (state) {
            _this.isTutorialActive = selectIsTutorialActive(state);
            return {
                centerPosition: state.viewport.centerPosition,
                zoomRequested: state.viewport.zoomRequested,
            };
        };
        _this.mapDispatch = function (dispatch) {
            return ({
                onViewportCentered: function () { return dispatch(actionViewportRequestClearCenter()); },
            });
        };
        _this.update = function (props) {
            if (props.centerPosition == undefined && !props.zoomRequested) {
                return;
            }
            if (props.centerPosition) {
                _this.subscribeCentering(props);
            }
            else if (props.zoomRequested) {
                _this.subscribeZooming();
            }
        };
        _this.dispose = function () {
            _this.resizeSubscription$.unsubscribe();
            _this.viewport.destroy(true);
            _this.isoSorter.destroy(true);
            return _this;
        };
        _this.subscribeCentering = function (props) {
            if (props.centerPosition === undefined) {
                return;
            }
            if (_this.viewportAnimationSubscription$ !== undefined) {
                _this.viewportAnimationSubscription$.unsubscribe();
            }
            _this.viewport.interactive = false;
            _this.viewport.interactiveChildren = false;
            var dest = _this.viewport.toLocal(new Point(props.centerPosition.x, props.centerPosition.y));
            var _a = _this.viewport.center, currentX = _a.x, currentY = _a.y;
            // TODO: cleanup the animation for Viewport
            var delta = 0;
            var stepCount = (60 * _this.ViewportAnimationTimeMs) / 1000;
            _this.viewportAnimationSubscription$ = utilTick.frame$.subscribe(function () {
                delta += 1 / stepCount;
                _this.viewport.moveCenter(utilNumber.lerp(currentX, dest.x, delta), utilNumber.lerp(currentY, dest.y, delta));
                _this.viewport.emit("moved", _this.viewport);
                if (Math.abs(1 - delta) <= 0.01) {
                    _this.viewportAnimationSubscription$.unsubscribe();
                    _this.viewport.interactive = true;
                    _this.viewport.interactiveChildren = true;
                    if (props.onViewportCentered != undefined) {
                        props.onViewportCentered();
                    }
                    if (props.zoomRequested) {
                        _this.subscribeZooming();
                    }
                }
            });
        };
        _this.subscribeZooming = function () {
            _this.viewport.interactive = true;
            _this.viewport.interactiveChildren = false;
            var step = 0;
            var lastZoomingWidth = 0;
            var stepCount = 40;
            var zoomPercentage = 1 / stepCount;
            var _a = _this.viewport.center, x = _a.x, y = _a.y;
            _this.viewportAnimationSubscription$ = utilTick.frame$.subscribe(function () {
                step++;
                var _a = _this.viewport.center, currentX = _a.x, currentY = _a.y;
                var zoomingWidthAfterStep = _this.viewport.zoomPercent(zoomPercentage, true).width;
                _this.viewport.emit("zoomed", _this.viewport);
                if (step === 60 ||
                    zoomingWidthAfterStep <= lastZoomingWidth ||
                    x !== currentX ||
                    y !== currentY) {
                    _this.viewportAnimationSubscription$.unsubscribe();
                    _this.viewport.interactiveChildren = true;
                }
                else {
                    lastZoomingWidth = zoomingWidthAfterStep;
                }
            });
        };
        _this.enableViewport = function () {
            if (_this.shouldEnableViewport && !_this.isTutorialActive) {
                _this.shouldEnableViewport = false;
                _this.viewport
                    .drag()
                    .clamp({ direction: "all" })
                    .wheel()
                    .pinch();
            }
        };
        _this.initViewport = function (config) {
            var _a;
            var _b = config ? config : _this.calculateWorldSize(_this.layers), worldHeight = _b.worldHeight, worldWidth = _b.worldWidth;
            _this.viewport = new Viewport({
                divWheel: config ? config.divWheel : undefined,
                screenHeight: window.innerHeight,
                screenWidth: window.innerWidth,
                worldHeight: worldHeight,
                worldWidth: worldWidth,
            });
            if (config != undefined) {
                _this.viewport.top = config.top;
                _this.viewport.left = config.left;
                if (config.zoom != undefined) {
                    _this.viewport.clampZoom({
                        maxHeight: config.zoom.max.y,
                        maxWidth: config.zoom.max.x,
                        minHeight: config.zoom.min.y,
                        minWidth: config.zoom.min.x,
                    });
                    _this.viewport.fit(true, config.zoom.initial.x, config.zoom.initial.y);
                }
                else {
                    _this.viewport.clampZoom({
                        maxHeight: worldHeight,
                        maxWidth: worldWidth,
                        minHeight: worldHeight * 0.5,
                        minWidth: worldWidth * 0.5,
                    });
                }
            }
            if (_this.layers) {
                // @ts-ignore
                (_a = _this.viewport).addChild.apply(_a, _this.layers);
                _this.viewport.addChildAt(_this.isoSorter, Math.min(_this.isoSorterZIndex, _this.viewport.children.length));
            }
            _this.addChild(_this.viewport);
        };
        _this.onResize = function () {
            _this.viewport.screenWidth = window.innerWidth;
            _this.viewport.screenHeight = window.innerHeight;
        };
        _this.calculateWorldSize = function (layers) {
            var worldSize = { worldWidth: 0, worldHeight: 0 };
            if (layers) {
                layers.reduce(function (acc, displayObject) {
                    acc.worldHeight = Math.max(acc.worldHeight, displayObject.height);
                    acc.worldWidth = Math.max(acc.worldWidth, displayObject.width);
                    return acc;
                }, worldSize);
            }
            return worldSize;
        };
        if (config != undefined) {
            if (config.layers != undefined) {
                _this.layers = config.layers.map(function (layerConfig, index) {
                    var layer = new layerConfig.type();
                    if (layerConfig.isoSortingGroup != undefined &&
                        layerConfig.isoSortingGroup !== IdIsoSortingGroup.None) {
                        if (_this.isoSorterZIndex === Number.MAX_SAFE_INTEGER) {
                            _this.isoSorterZIndex = index;
                        }
                        layer.sortingGroup = layerConfig.isoSortingGroup;
                    }
                    if (layerConfig.id !== undefined) {
                        Object.assign(layer, { id: layerConfig.id });
                    }
                    if (layerConfig.interactive != undefined) {
                        layer.interactive = layerConfig.interactive;
                        layer.interactiveChildren = layerConfig.interactive;
                    }
                    if (config.viewport != undefined && layer["setWorldDimensions"] !== undefined) {
                        var sceneLayer = layer;
                        sceneLayer.setWorldDimensions(config.viewport.worldWidth, config.viewport.worldHeight);
                    }
                    layer.position.set(layerConfig.x, layerConfig.y);
                    return layer;
                });
                _this.isoSorter = new IsoSorter(_this.layers);
            }
            _this.initViewport(config.viewport);
        }
        _this.resizeSubscription$ = resize$.subscribe(_this.onResize);
        return _this;
    }
    return SceneCanvas;
}(CanvasConnectedComponent));
export { SceneCanvas };



//////////////////
// WEBPACK FOOTER
// ./src/features/scenes/SceneCanvas.ts
// module id = 2225
// module chunks = 3